import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { didCancel } from 'ember-concurrency'
import { isAbortError } from 'teacher-ui-client/errors/http-error'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type { BookClassification } from 'teacher-ui-client/models/book-library/book'
import type { BookSearchQueryFilter, ContinueSearchTask } from 'teacher-ui-client/services/book-library/book-finder'
import { TrackedArray } from 'tracked-built-ins'

/**
 * Represents a search result that will grow as more pages of data are loaded. It must be provided an array of initial
 * books (even if empty) and a total that is the count of books expected if all pages were loaded. This is so the result
 * can know when it is "fulfilled" and no more books need to be loaded into it.
 *
 * @param {Book[]} initialBooks - the classification of books this pager represents
 * @param {Number} total - the search filters that this pager can use to obtain the next page of data
 */
export class SearchResult {
  // tracked, so new books loaded in as the user scrolls across display in the UI, without re-rendering existing books
  books: BookLibraryBook[] = new TrackedArray([])

  total: number

  constructor(initialBooks: BookLibraryBook[], total: number) {
    this.total = total // total count of books expected to be loaded in this search result when fulfilled
    this.loadBooks(initialBooks)
  }

  // count of books loaded in this search result
  get loadedCount() {
    return this.books.length
  }

  // true if all expected books have been loaded
  get isFulfilled() {
    return this.loadedCount >= this.total
  }

  // true if there are any books loaded yet for this search result
  get hasAnyBooks() {
    return this.loadedCount > 0
  }

  get emptyResult() {
    return this.total === 0
  }

  loadBooks(books: BookLibraryBook[]) {
    this.books.push(...books)
  }
}

/**
 * Manages a growing search result, which is filled as new pages of data are loaded into the pager.
 * Represents a scrollable element in the UI
 *
 * @param {BookClassification} classification - the classification of books this pager represents
 * @param {BookSearchQueryFilter} filters - the search filters that this pager can use to obtain the next page of data
 * @param {Task} loadPageTask - an ember-concurrency task that can be performed with the filters & the offset
 * to return the next result
 */
export class SearchPager {
  @tracked result?: SearchResult

  classification: BookClassification

  filters: BookSearchQueryFilter

  loadPageTask: ContinueSearchTask

  constructor(classification: BookClassification, filters: BookSearchQueryFilter, loadPageTask: ContinueSearchTask) {
    this.classification = classification
    this.filters = filters
    this.loadPageTask = loadPageTask
  }

  get hasResult() {
    return Boolean(this.result)
  }

  get isFulfilled() {
    return Boolean(this.result?.isFulfilled)
  }

  get hasAnyBooks() {
    return Boolean(this.result?.hasAnyBooks)
  }

  get emptyResult() {
    return Boolean(this.result?.emptyResult)
  }

  get books() {
    return this.result?.books
  }

  @action
  async loadNextPage() {
    if (!this.isFulfilled) {
      const offset = this.result?.loadedCount ?? 0
      try {
        const { books, total } = await this.loadPageTask.perform(this.filters, offset)
        if (!this.result) {
          this.result = new SearchResult(books, total)
        } else if (books.length > 0) {
          this.result.loadBooks(books)
        }
      } catch (error: unknown) {
        const wasAborted = isAbortError(error)
        const wasCancelled = didCancel(error)

        if (wasAborted || wasCancelled) {
          // do nothing
        } else {
          throw error
        }
      }
    }
  }
}

/**
 * Manages an array of scrollable pagers, providing various helper getters to know the state of those pagers.
 * Represents a search panel in the UI
 *
 * @param {String} name - the display name of the group
 * @param {SearchPager[]} pagers - the pagers this group manages
 */
export class SearchGroup {
  name: string

  pagers: SearchPager[]

  constructor(name: string, pagers: SearchPager[] = []) {
    this.name = name
    this.pagers = pagers
  }

  get hasAnyBooks() {
    return this.pagers.some((pager) => pager.hasAnyBooks)
  }

  // true if all pagers have a result available
  get hasAllResults() {
    return this.pagers.every((pager) => pager.hasResult)
  }

  // true if all pagers do not have result available
  get hasNoResults() {
    return this.pagers.every((pager) => !pager.hasResult)
  }

  // true if all pagers are reporting empty (there's zero books and no more to get)
  get allPagersEmpty() {
    return this.pagers.every((pager) => pager.emptyResult)
  }

  // Only return a value if a result is available for all pagers.
  get total() {
    if (this.hasAllResults) {
      return this.pagers.reduce((total, pager) => {
        return total + (pager.result?.total ?? 0)
      }, 0)
    } else {
      return null
    }
  }
}
